<template>
    <v-row :key="updateKey">
        <v-col cols="12">
            <h2 class="mt-5 mb-4">{{ title }}</h2>
            <v-select
                v-model="details.workingCircumstance"
                :items="workingCircumstanceChoices"
                item-text="name"
                item-value="id"
                label="What is your current employment status?"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :rules="rules.workingCircumstance"
                :error="getError(`applicants.${index}.workingCircumstance`)"
                :error-messages="
                    getErrorMessages(`applicants.${index}.workingCircumstance`)
                "
                @input="
                    removeErrorMessage(
                        `applicants.${index}.workingCircumstance`
                    )
                "
                @change="circumstanceChanged"
            ></v-select>

            <v-menu
                ref="employmentStartDateMenuRef"
                v-model="showEmploymentStartDatePicker"
                v-if="details.workingCircumstance && isWorking"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        v-model="employmentStartDateFormatted"
                        label="Employment start date"
                        append-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        required
                        hide-details="auto"
                        class="rounded-0 mb-4 v-input--required"
                        :rules="rules.employmentStartDate"
                        :error="
                            getError(`applicants.${index}.employmentStartDate`)
                        "
                        :error-messages="
                            getErrorMessages(
                                `applicants.${index}.employmentStartDate`
                            )
                        "
                        @input="
                            removeErrorMessage(
                                `applicants.${index}.employmentStartDate`
                            )
                        "
                    ></v-text-field>
                </template>
                <v-date-picker
                    v-model="details.employmentStartDate"
                    :max="
                        new Date(
                            Date.now() - new Date().getTimezoneOffset() * 60000
                        )
                            .toISOString()
                            .substr(0, 10)
                    "
                    min="1900-01-01"
                    :active-picker.sync="employmentStartDateActivePicker"
                    @change="saveEmploymentStartDatePicker"
                ></v-date-picker>
            </v-menu>

            <v-text-field
                v-model="details.employerName"
                label="Employer name"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                v-if="
                    details.workingCircumstance && isWorking && !isSelfEmployed
                "
                :rules="rules.employerName"
                :error="getError(`applicants.${index}.employerName`)"
                :error-messages="
                    getErrorMessages(`applicants.${index}.employerName`)
                "
                @input="removeErrorMessage(`applicants.${index}.employerName`)"
            ></v-text-field>

            <v-text-field
                v-model="details.occupation"
                label="Job Title"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                v-if="
                    details.workingCircumstance && isWorking && !isSelfEmployed
                "
                :rules="rules.occupation"
                :error="getError(`applicants.${index}.occupation`)"
                :error-messages="
                    getErrorMessages(`applicants.${index}.occupation`)
                "
                @input="removeErrorMessage(`applicants.${index}.occupation`)"
            ></v-text-field>

            <v-text-field
                v-if="
                    details.workingCircumstance && isWorking && !isSelfEmployed
                "
                v-model="details.employersContactNumber"
                label="Employer's contact number"
                outlined
                hide-details="auto"
                loading="true"
                class="rounded-0 mb-4"
                :rules="rules.employersContactNumber"
                :error="getError(`applicants.${index}.employersContactNumber`)"
                :error-messages="
                    getErrorMessages(
                        `applicants.${index}.employersContactNumber`
                    )
                "
                @input="
                    removeErrorMessage(
                        `applicants.${index}.employersContactNumber`
                    )
                "
            ></v-text-field>

            <v-text-field
                v-if="
                    details.workingCircumstance && isWorking && !isSelfEmployed
                "
                v-model="details.employersContactEmail"
                label="Employer's contact email"
                outlined
                hide-details="auto"
                loading="true"
                class="rounded-0 mb-4"
                :rules="rules.employersContactEmail"
                :error="getError(`applicants.${index}.employersContactEmail`)"
                :error-messages="
                    getErrorMessages(
                        `applicants.${index}.employersContactEmail`
                    )
                "
                @input="
                    removeErrorMessage(
                        `applicants.${index}.employersContactEmail`
                    )
                "
            ></v-text-field>

            <v-text-field
                v-if="
                    details.workingCircumstance && isWorking && !isSelfEmployed
                "
                v-model="details.workAddressLine1"
                label="Building name or number"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :rules="rules.workAddressLine1"
                :error="getError(`applicants.${index}.workAddressLine1`)"
                :error-messages="
                    getErrorMessages(`applicants.${index}.workAddressLine1`)
                "
                @input="
                    removeErrorMessage(`applicants.${index}.workAddressLine1`)
                "
            ></v-text-field>

            <v-text-field
                v-if="
                    details.workingCircumstance && isWorking && !isSelfEmployed
                "
                v-model="details.workAddressLine2"
                label="Street address"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :rules="rules.workAddressLine2"
                :error="getError(`applicants.${index}.workAddressLine2`)"
                :error-messages="
                    getErrorMessages(`applicants.${index}.workAddressLine2`)
                "
                @input="
                    removeErrorMessage(`applicants.${index}.workAddressLine2`)
                "
            ></v-text-field>

            <div
                class="d-flex"
                v-if="
                    details.workingCircumstance && isWorking && !isSelfEmployed
                "
            >
                <v-text-field
                    v-model="details.workPostcode"
                    label="Work postcode"
                    outlined
                    required
                    hide-details="auto"
                    class="rounded-0 mb-4 mr-4 v-input--required"
                    :rules="rules.workPostcode"
                    :error="getError(`applicants.${index}.workPostcode`)"
                    :error-messages="
                        getErrorMessages(`applicants.${index}.workPostcode`)
                    "
                    @input="
                        removeErrorMessage(`applicants.${index}.workPostcode`)
                    "
                >
                    <template v-slot:append>
                        <v-menu
                            open-on-hover
                            origin="center center"
                            transition="scale-transition"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn icon v-bind="attrs" v-on="on">
                                    <v-icon>mdi-help-box</v-icon>
                                </v-btn>
                            </template>

                            <v-card elevation="2" max-width="260">
                                <v-card-text>
                                    Tell us the postcode of where you normally
                                    go to work.
                                </v-card-text>
                            </v-card>
                        </v-menu>
                    </template>
                </v-text-field>
                <v-btn
                    @click="searchPostcode('workPostcode')"
                    small
                    color="primary"
                    class="mt-4"
                    v-if="!formDisabled"
                >
                    Search
                </v-btn>
            </div>

            <v-select
                v-if="
                    details.workingCircumstance && isWorking && !isSelfEmployed
                "
                v-model="details.workLocalAuthorityId"
                :items="localAuthorityChoices"
                label="Work local authority"
                outlined
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                item-text="name"
                item-value="id"
                :rules="rules.workLocalAuthority"
                :error="getError(`applicants.${index}.workLocalAuthorityId`)"
                :error-messages="
                    getErrorMessages(`applicants.${index}.workLocalAuthorityId`)
                "
                @input="
                    removeErrorMessage(
                        `applicants.${index}.workLocalAuthorityId`
                    )
                "
            ></v-select>

            <v-text-field
                v-if="
                    details.workingCircumstance && isWorking && !isSelfEmployed
                "
                v-model="details.workTown"
                label="Town"
                outlined
                hide-details="auto"
                loading="true"
                class="rounded-0 mb-4"
                :error="getError(`applicants.${index}.workTown`)"
                :error-messages="
                    getErrorMessages(`applicants.${index}.workTown`)
                "
                @input="removeErrorMessage(`applicants.${index}.workTown`)"
            ></v-text-field>

            <v-select
                v-if="
                    details.workingCircumstance && isWorking && !isSelfEmployed
                "
                v-model="details.workCounty"
                :items="countyChoices"
                label="County"
                outlined
                hide-details="auto"
                class="rounded-0 mb-4"
                item-text="name"
                item-value="id"
                :error="getError(`applicants.${index}.workCounty`)"
                :error-messages="
                    getErrorMessages(`applicants.${index}.workCounty`)
                "
                @input="removeErrorMessage(`applicants.${index}.workCounty`)"
            ></v-select>
        </v-col>
    </v-row>
</template>

<script>
import {
    defineComponent,
    ref,
    onMounted,
    computed,
    watch,
    nextTick,
} from "@vue/composition-api";
import { find, forEach, has, merge } from "lodash";
import HelperServices from "../../../services/HelperServices";

export default defineComponent({
    name: "Employment",
    props: [
        "index",
        "accessToken",
        "title",
        "getError",
        "getErrorMessages",
        "removeErrorMessage",
        "loggedInProfile",
        "value",
        "formDisabled",
    ],
    setup(props, { emit }) {
        const accessToken = ref(null);
        const details = ref({
            workingCircumstance: null,
            occupation: null,
            employmentStartDate: null,
            employerName: null,
            workAddressLine1: null,
            workAddressLine2: null,
            workPostcode: null,
            workTown: null,
            workCounty: null,
            workLocalAuthorityId: null,
            employersContactNumber: null,
            employersContactEmail: null,
            isWorking: false,
        });
        const workingCircumstanceChoices = ref([]);
        const showEmploymentStartDatePicker = ref(false);
        const employmentStartDateMenuRef = ref(null);
        const employmentStartDateActivePicker = ref(null);
        const updateKey = ref(0);

        const rules = ref({
            workingCircumstance: [
                (v) => !!v || "Working circumstance is required",
            ],
            occupation: [(v) => !!v || "Job Title is required"],
            employerName: [(v) => !!v || "Employer name is required"],
            workAddressLine1: [
                (v) => !!v || "Building name or number is required",
            ],
            workAddressLine2: [(v) => !!v || "Street address is required"],
            employmentStartDate: [
                (v) => !!v || "Employment start date is required",
                (v) =>
                    /(0?[1-9]|[12][0-9]|3[01])[/.](0?[1-9]|1[012])[/.](19|20)\d\d/.test(
                        v
                    ) || "Please enter a valid date (DD/MM/YYYY)",
            ],
            employersContactNumber: [
                (v) =>
                    !v ||
                    /^(\s*|\d+)$/.test(v) ||
                    "Please enter a valid phone number",
            ],
            employersContactEmail: [
                (v) => !v || /.+@.+/.test(v) || "Please enter a valid email",
            ],
            workPostcode: [
                (v) => !!v || "Work postcode is required",
                (v) =>
                    /^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/.test(
                        v
                    ) || "Please enter a valid postcode",
            ],
            workLocalAuthority: [(v) => !!v || "Local authority is required"],
        });

        const localAuthorityChoices = ref([]);
        const countyChoices = ref([]);

        const isWorking = computed(() => {
            if (details.value.workingCircumstance) {
                let circumstanceChoice = find(
                    workingCircumstanceChoices.value,
                    { id: details.value.workingCircumstance }
                );
                if (circumstanceChoice.working) {
                    return true;
                }
                return false;
            }
            return false;
        });

        const isSelfEmployed = computed(() => {
            if (details.value.workingCircumstance.id == 9) {
                return true;
            }
            return false;
        });

        function setFields() {
            let currentFields = {};
            forEach(details.value, function (detValue, detKey) {
                if (has(props, "value." + detKey)) {
                    currentFields[detKey] = props.value[detKey];
                } else {
                    currentFields[detKey] = detValue;
                }
            });
            if (props.index == 1 && props.loggedInProfile) {
                if (isEmpty(currentFields.occupation)) {
                    currentFields.occupation = props.loggedInProfile.occupation;
                }
            }

            details.value = currentFields;

            if (isWorking.value) {
                currentFields["isWorking"] = true;
            }
        }

        onMounted(() => {
            init();
        });

        function init() {
            if (props.accessToken !== "") {
                accessToken.value = props.accessToken;
            }
            Promise.all([
                fetchWorkingCircumstances(),
                fetchLocalAuthorities(),
                fetchCounties(),
            ]).then(() => {
                setFields();
                emit("input", props.index, details.value);
                emit("toggleLoading", false);
            });
        }

        function fetchWorkingCircumstances() {
            return new Promise(function (resolve, reject) {
                emit("toggleLoading", true);
                HelperServices.getWorkingCircumstances()
                    .then(function (response) {
                        if (response.data.data) {
                            workingCircumstanceChoices.value =
                                response.data.data;
                        }
                        resolve(response);
                    })
                    .catch(function (error) {
                        console.error(error);
                        reject(error);
                    })
                    .then(function () {
                        emit("toggleLoading", false);
                    });
            });
        }

        function fetchLocalAuthorities() {
            return new Promise(function (resolve, reject) {
                emit("toggleLoading", true);
                HelperServices.getLocalAuthorities()
                    .then(function (response) {
                        if (response.data.data) {
                            localAuthorityChoices.value = response.data.data;
                        }
                        resolve(response);
                    })
                    .catch(function (error) {
                        console.error(error);
                        reject(error);
                    })
                    .then(function () {
                        emit("toggleLoading", false);
                    });
            });
        }

        function fetchCounties() {
            return new Promise(function (resolve, reject) {
                emit("toggleLoading", true);
                HelperServices.getCounties()
                    .then(function (response) {
                        if (response.data.data) {
                            countyChoices.value = response.data.data;
                        }
                        resolve(response);
                    })
                    .catch(function (error) {
                        console.error(error);
                        reject(error);
                    })
                    .then(function () {
                        emit("toggleLoading", false);
                    });
            });
        }

        const employmentStartDateFormatted = computed(() => {
            return formatDate(details.value.employmentStartDate);
        });

        function formatDate(date) {
            if (!date) return null;
            const [year, month, day] = date.split("-");
            return `${day}/${month}/${year}`;
        }

        function saveEmploymentStartDatePicker(date) {
            employmentStartDateMenuRef.value.save(date);
        }

        function searchPostcode(field) {
            let fieldRef = details.value[field];
            HelperServices.getLocalAuthorityFromPostcode(fieldRef)
                .then(function (response) {
                    if (has(response, "data.id")) {
                        if (field == "workPostcode") {
                            details.value.workLocalAuthorityId =
                                response.data.id;
                            incrementUpdateKey();
                        }
                    }
                    if (has(response, "data.name")) {
                        if (field == "workPostcode") {
                            details.value.workTown = response.data.name;
                            incrementUpdateKey();
                        }
                    }
                    if (has(response, "data.county.id")) {
                        if (field == "workPostcode") {
                            details.value.workCounty = response.data.county.id;
                            incrementUpdateKey();
                        }
                    }
                })
                .catch(function (error) {
                    console.error(error);
                });
        }

        function circumstanceChanged() {
            if (isWorking.value === false) {
                details.value.occupation = null;
                details.value.employmentStartDate = null;
                details.value.employerName = null;
                details.value.workPostcode = null;
                details.value.workLocalAuthorityId = null;
                details.value.isWorking = false;
            } else {
                details.value.isWorking = true;
            }
        }

        function incrementUpdateKey() {
            updateKey.value = updateKey.value + 1;
        }

        watch(
            details,
            (newData, oldData) => {
                emit("input", props.index, newData);
            },
            {
                deep: true,
            }
        );

        watch(showEmploymentStartDatePicker, (currentValue) => {
            currentValue &&
                setTimeout(
                    () => (employmentStartDateActivePicker.value = "YEAR")
                );
        });

        return {
            details,
            rules,
            workingCircumstanceChoices,
            employmentStartDateFormatted,
            showEmploymentStartDatePicker,
            employmentStartDateMenuRef,
            employmentStartDateActivePicker,
            localAuthorityChoices,
            updateKey,
            countyChoices,
            isWorking,
            isSelfEmployed,
            formatDate,
            saveEmploymentStartDatePicker,
            searchPostcode,
            circumstanceChanged,
        };
    },
});
</script>
